import { useCallback, useEffect, useRef, useState } from "react";
import { serverMessageSchema, type ServerMessage, type ThrowSession } from "@airspace/shared";
import { ApiRequestError, api, type IncomingRequest } from "./client.js";

export type ConnectionState = "connecting" | "online" | "offline";

export interface ThrowState {
  session: ThrowSession | null;
  incomingRequests: IncomingRequest[];
  connection: ConnectionState;
  /** Gesetzt, solange auf die Antwort des Gegenuebers gewartet wird. */
  outgoingRequestId: string | null;
  loadError: string | null;
}

/**
 * Haelt den Throw-Zustand des Browsers.
 *
 * Zwei Quellen, bewusst getrennt:
 * - Der REST-Endpunkt liefert den vollstaendigen Zustand. Er ist die Wahrheit
 *   und wird beim Laden sowie nach jeder Unterbrechung erneut abgefragt.
 * - Der WebSocket liefert Aenderungen. Er ist schnell, aber nicht verlaesslich:
 *   waehrend einer Unterbrechung verpasste Nachrichten kommen nicht nach.
 *
 * Deshalb wird nach jedem Wiederverbinden neu geladen, statt auf eine
 * lueckenlose Nachrichtenfolge zu vertrauen.
 */
export function useThrowSession() {
  const [state, setState] = useState<ThrowState>({
    session: null,
    incomingRequests: [],
    connection: "connecting",
    outgoingRequestId: null,
    loadError: null,
  });

  // In einer Ref, damit der WebSocket-Effekt nicht bei jeder Zustandsaenderung
  // neu aufgebaut wird -- das wuerde die Verbindung staendig abreissen lassen.
  const reload = useRef<() => void>(() => {});

  /**
   * Abonnenten fuer eingehende Servernachrichten.
   *
   * Damit kann der Transferteil dieselbe WebSocket-Verbindung mitlesen, ohne
   * eine zweite aufzubauen -- und ohne dass dieser Hook etwas ueber Dateien
   * wissen muss.
   */
  const subscribers = useRef(new Set<(message: ServerMessage) => void>());

  const subscribe = useCallback((handler: (message: ServerMessage) => void) => {
    subscribers.current.add(handler);
    return () => {
      subscribers.current.delete(handler);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await api.getSession();
      setState((current) => ({
        ...current,
        session: data.session,
        incomingRequests: data.incomingRequests,
        loadError: null,
        // Besteht eine Kopplung, ist eine eigene offene Anfrage gegenstandslos.
        outgoingRequestId: data.session.pairing ? null : current.outgoingRequestId,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loadError:
          error instanceof ApiRequestError
            ? error.message
            : "Der Sitzungszustand konnte nicht geladen werden.",
      }));
    }
  }, []);

  reload.current = () => void load();

  useEffect(() => {
    void load();
  }, [load]);

  // --- WebSocket -------------------------------------------------------------

  useEffect(() => {
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let heartbeat: ReturnType<typeof setInterval> | undefined;
    let attempt = 0;
    let closed = false;

    const connect = (): void => {
      if (closed) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(`${protocol}//${window.location.host}/api/throw/events`);

      socket.onopen = () => {
        attempt = 0;
        setState((current) => ({ ...current, connection: "online" }));
        // Waehrend der Unterbrechung koennen Nachrichten verloren gegangen
        // sein -- deshalb den vollstaendigen Zustand neu holen.
        reload.current();

        heartbeat = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 25_000);
      };

      socket.onmessage = (event) => {
        let parsed: unknown;
        try {
          parsed = JSON.parse(String(event.data));
        } catch {
          return;
        }
        const message = serverMessageSchema.safeParse(parsed);
        // Unbekannte Nachrichten still verwerfen: eine neuere Serverversion
        // darf Nachrichtenarten ergaenzen, ohne alte Clients zu stoeren.
        if (message.success) handleMessage(message.data);
      };

      socket.onclose = () => {
        if (heartbeat) clearInterval(heartbeat);
        if (closed) return;
        setState((current) => ({ ...current, connection: "offline" }));

        // Exponentieller Rückzug mit Deckel: nach einem Serverneustart soll
        // sich der Client zuegig wieder melden, bei laengerem Ausfall aber
        // nicht im Sekundentakt anklopfen.
        attempt += 1;
        const delay = Math.min(1000 * 2 ** (attempt - 1), 15_000);
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // Der Fehler wird ohnehin von `onclose` gefolgt; dort steht die Logik.
      };
    };

    const handleMessage = (message: ServerMessage): void => {
      for (const handler of subscribers.current) {
        try {
          handler(message);
        } catch {
          // Ein fehlerhafter Abonnent darf die uebrigen nicht ausbremsen.
        }
      }

      switch (message.type) {
        case "request.incoming":
          setState((current) =>
            current.incomingRequests.some((r) => r.requestId === message.requestId)
              ? current
              : {
                  ...current,
                  incomingRequests: [
                    ...current.incomingRequests,
                    {
                      requestId: message.requestId,
                      fromName: message.fromName,
                      createdAt: message.createdAt,
                      expiresAt: message.expiresAt,
                    },
                  ],
                },
          );
          break;

        case "request.resolved":
          setState((current) => ({
            ...current,
            incomingRequests: current.incomingRequests.filter(
              (r) => r.requestId !== message.requestId,
            ),
            outgoingRequestId:
              current.outgoingRequestId === message.requestId ? null : current.outgoingRequestId,
          }));
          break;

        case "pairing.created":
          setState((current) => ({
            ...current,
            outgoingRequestId: null,
            incomingRequests: [],
            session: current.session
              ? {
                  ...current.session,
                  pin: null,
                  pinExpiresAt: null,
                  pairing: {
                    pairingId: message.pairingId,
                    peerName: message.peerName,
                    expiresAt: message.expiresAt,
                  },
                }
              : current.session,
          }));
          break;

        case "pairing.closed":
          // Nach dem Ende der Kopplung vergibt der Server eine neue PIN. Statt
          // sie hier zu erraten, wird der Zustand frisch geladen.
          setState((current) => ({
            ...current,
            session: current.session ? { ...current.session, pairing: null } : current.session,
          }));
          reload.current();
          break;

        case "pin.changed":
          setState((current) => ({
            ...current,
            session: current.session
              ? { ...current.session, pin: message.pin, pinExpiresAt: message.expiresAt }
              : current.session,
          }));
          break;

        case "file.offered":
        case "file.status":
          // Werden vom Transferteil ueber `subscribe` behandelt.
          break;

        case "error":
        case "pong":
          break;
      }
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeat) clearInterval(heartbeat);
      socket?.close();
    };
  }, []);

  // --- Aktionen --------------------------------------------------------------

  const connect = useCallback(async (pin: string) => {
    const result = await api.connect(pin);
    if (result.kind === "request") {
      setState((current) => ({ ...current, outgoingRequestId: result.requestId }));
    }
    return result;
  }, []);

  const accept = useCallback(async (requestId: string) => {
    await api.acceptRequest(requestId);
    // Der Zustand kommt gleich als pairing.created ueber den WebSocket, aber
    // ein Neuladen macht die Oberflaeche auch dann korrekt, wenn die
    // Verbindung gerade haengt.
    reload.current();
  }, []);

  const reject = useCallback(async (requestId: string) => {
    await api.rejectRequest(requestId);
    setState((current) => ({
      ...current,
      incomingRequests: current.incomingRequests.filter((r) => r.requestId !== requestId),
    }));
  }, []);

  const leave = useCallback(async () => {
    await api.leavePairing();
    reload.current();
  }, []);

  const renewPin = useCallback(async () => {
    const result = await api.renewPin();
    setState((current) => ({
      ...current,
      session: current.session
        ? { ...current.session, pin: result.pin, pinExpiresAt: result.expiresAt }
        : current.session,
    }));
  }, []);

  return {
    state,
    subscribe,
    actions: { connect, accept, reject, leave, renewPin, reload: () => reload.current() },
  };
}
